/**
 * OnbWelcome — onboarding step 1.
 *
 * Hero mark + h1 + lede, then a primary "Continue" and a quiet "Skip". The mark
 * (the motif-backed tile) is injected via `renderMark` so the flow owns the
 * `GMotif` positioning.
 *
 * 2026-08: the heading takes the display voice (`font-display`, Onest since
 * 2026-10-09) — this is one of the
 * two screens in the flow that earns it — and the lede is replaced by three
 * ruled promises rather than one vague sentence. A first-time renovator opening
 * an unfamiliar app needs to know what it will do for them BEFORE being asked
 * for an email; "tell us about your flat" was asking for trust it had not yet
 * earned. The three lines are the product's actual promise: name the material,
 * price it locally, say which stall.
 */
import { type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@garden/ui";

import { ONBOARDING_NS } from "@/features/onboarding/api";

export interface OnbWelcomeProps {
  onContinue: () => void;
  onSkip: () => void;
  /** The hero mark (motif-backed tile) supplied by the flow. */
  renderMark: () => ReactNode;
}

/** What GardEn will actually do — stated as line items, not as a paragraph. */
const PROMISE_KEYS = [
  "welcome.promises.name",
  "welcome.promises.price",
  "welcome.promises.place",
] as const;

export function OnbWelcome({
  onContinue,
  onSkip,
  renderMark,
}: OnbWelcomeProps): ReactElement {
  const { t } = useTranslation(ONBOARDING_NS);
  return (
    <div className="flex flex-1 flex-col" data-testid="onb-welcome">
      <div className="flex flex-1 flex-col items-center justify-center py-4 text-center">
        {renderMark()}
        <h1 className="font-display m-0 text-[28px] font-extrabold leading-[1.08] text-foreground">
          {t("welcome.h1")}
        </h1>
        <p className="mx-auto mt-3 max-w-[36ch] text-[15px] leading-[1.5] text-[var(--g-ink-2)]">
          {t("welcome.lede")}
        </p>

        <ul className="g-ruled mt-5 w-full overflow-hidden rounded-[var(--g-radius-lg)] border border-[var(--g-rule)] bg-[var(--g-surface)] text-left sm:mt-7">
          {PROMISE_KEYS.map((key) => (
            <li
              key={key}
              className="px-4 py-3 text-[14px] leading-snug text-foreground sm:py-3.5"
            >
              {t(key)}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col gap-2.5 pt-6">
        <Button
          size="lg"
          full
          trailing="arrowR"
          data-testid="onb-welcome-continue"
          onClick={onContinue}
        >
          {t("cta.continue")}
        </Button>
        <Button
          size="lg"
          variant="ghost"
          full
          data-testid="onb-welcome-skip"
          onClick={onSkip}
        >
          {t("cta.skip")}
        </Button>
      </div>
    </div>
  );
}

export default OnbWelcome;
